/**
 * Normalizes Google Places payloads for the combined place modal Overview tab.
 * Supports Places API (New) shapes and legacy opening_hours.weekday_text.
 */

import type { Location } from '@/models/locationModel';

function asRecord(v: unknown): Record<string, unknown> | undefined {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return undefined;
}

function pickStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

/**
 * Prefer `regularOpeningHours` for weekly lines (Place Details full mask), then
 * `currentOpeningHours`, then legacy `opening_hours` / `openingHours`.
 */
function readOpeningBlock(gpd: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!gpd) return undefined;
  const candidates: (Record<string, unknown> | undefined)[] = [
    asRecord(gpd.regularOpeningHours) ?? asRecord(gpd.regular_opening_hours),
    asRecord(gpd.currentOpeningHours) ?? asRecord(gpd.current_opening_hours),
    asRecord(gpd.opening_hours) ?? asRecord(gpd.openingHours),
  ];
  for (const block of candidates) {
    if (!block) continue;
    const desc = pickStringArray(block.weekdayDescriptions ?? block.weekday_descriptions);
    if (desc.length > 0) return block;
    const wt = pickStringArray(block.weekday_text ?? block.weekdayText);
    if (wt.length > 0) return block;
  }
  return undefined;
}

function parseOpenNowField(block: Record<string, unknown>): boolean | null {
  const raw = block.openNow ?? block.open_now;
  if (typeof raw === 'boolean') return raw;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

function readOpenNowFromCurrentHours(gpd: Record<string, unknown> | undefined): boolean | null {
  if (!gpd) return null;
  const cur = asRecord(gpd.currentOpeningHours) ?? asRecord(gpd.current_opening_hours);
  if (!cur) return null;
  return parseOpenNowField(cur);
}

export interface GoogleOpeningHoursDisplay {
  /** One-line summary (first weekday line or first description). */
  summaryLine: string | null;
  /** Full week lines when available (for expand UI). */
  weekdayLines: string[];
  /** Places (New) open_now when present. */
  openNow: boolean | null;
}

/**
 * Extracts human-readable opening hours from google_places_data.
 * - Places (New): prefers `regularOpeningHours` for weekly lines, then `currentOpeningHours`;
 *   `openNow` may be taken from `currentOpeningHours` when the chosen block omits it.
 * - Legacy: `opening_hours.weekday_text[]`
 */
export function extractGoogleOpeningHoursDisplay(
  googlePlacesData: Record<string, unknown> | undefined | null,
): GoogleOpeningHoursDisplay {
  const gpd = googlePlacesData ?? undefined;
  const block = readOpeningBlock(gpd);
  if (!block) {
    return { summaryLine: null, weekdayLines: [], openNow: null };
  }

  const weekdayDescriptions = pickStringArray(block.weekdayDescriptions ?? block.weekday_descriptions);
  if (weekdayDescriptions.length > 0) {
    let openNow = parseOpenNowField(block);
    if (openNow === null) {
      openNow = readOpenNowFromCurrentHours(gpd);
    }
    return {
      summaryLine: weekdayDescriptions[0] ?? null,
      weekdayLines: weekdayDescriptions,
      openNow,
    };
  }

  const weekdayText = pickStringArray(block.weekday_text ?? block.weekdayText);
  if (weekdayText.length > 0) {
    return {
      summaryLine: weekdayText[0] ?? null,
      weekdayLines: weekdayText,
      openNow: readOpenNowFromCurrentHours(gpd),
    };
  }

  return { summaryLine: null, weekdayLines: [], openNow: null };
}

/** Google Money: `units` (string/int) + `nanos` + `currencyCode` (Places New). */
function readMoney(m: unknown): { amount: number; currencyCode: string } | null {
  const r = asRecord(m);
  if (!r) return null;
  const ccRaw = r.currencyCode ?? r.currency_code;
  const currencyCode =
    typeof ccRaw === 'string' && ccRaw.trim() ? ccRaw.trim().toUpperCase() : null;
  if (!currencyCode) return null;

  const units = r.units;
  let base = 0;
  if (typeof units === 'string' && units.trim()) {
    const n = Number(units);
    if (!Number.isFinite(n)) return null;
    base = n;
  } else if (typeof units === 'number' && Number.isFinite(units)) {
    base = units;
  } else {
    return null;
  }

  const nanosRaw = r.nanos;
  const nanos = typeof nanosRaw === 'number' && Number.isFinite(nanosRaw) ? nanosRaw : 0;
  const amount = base + nanos / 1e9;
  if (!Number.isFinite(amount)) return null;
  return { amount, currencyCode };
}

function formatMoneyAmount(amount: number, currencyCode: string, numberFormatLocale?: string): string {
  const fracDigits = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(numberFormatLocale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: fracDigits,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount}`;
  }
}

/**
 * Formats Places (New) `priceRange` using each Money's `currencyCode` and optional
 * `Intl` locale (e.g. browser `navigator.language`) for grouping/separator style.
 */
export function formatGooglePriceRange(
  googlePlacesData: Record<string, unknown> | undefined | null,
  numberFormatLocale?: string,
): string | null {
  const gpd = googlePlacesData ?? undefined;
  if (!gpd) return null;
  const pr = asRecord(gpd.priceRange ?? gpd.price_range);
  if (!pr) return null;
  const startM = readMoney(pr.startPrice ?? pr.start_price);
  const endM = readMoney(pr.endPrice ?? pr.end_price);
  const loc = numberFormatLocale;

  if (startM && endM) {
    return `${formatMoneyAmount(startM.amount, startM.currencyCode, loc)} \u2013 ${formatMoneyAmount(endM.amount, endM.currencyCode, loc)}`;
  }
  if (startM) {
    return `${formatMoneyAmount(startM.amount, startM.currencyCode, loc)}+`;
  }
  if (endM) {
    return `Up to ${formatMoneyAmount(endM.amount, endM.currencyCode, loc)}`;
  }
  return null;
}

function pickNumberish(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v && typeof v === 'object' && '$numberInt' in (v as object)) {
    const n = parseInt(String((v as { $numberInt: string }).$numberInt), 10);
    return Number.isFinite(n) ? n : null;
  }
  if (v && typeof v === 'object' && '$numberDouble' in (v as object)) {
    const n = parseFloat(String((v as { $numberDouble: string }).$numberDouble));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Google coarse price level on `google_places_data` only (1–4 → $ … $$$$).
 * Not the itinerary/list user `budget` field on the location root.
 */
function formatGooglePlacesPriceLevelDisplay(
  gpd: Record<string, unknown> | undefined | null,
): string | null {
  if (!gpd) return null;
  const pl = pickNumberish(gpd.priceLevel ?? gpd.price_level);
  if (pl == null || pl < 1 || pl > 4) return null;
  return '$'.repeat(pl);
}

/** Review count: Places (New) userRatingCount or legacy user_ratings_total. */
export function getGoogleUserRatingCountUnified(
  googlePlacesData: Record<string, unknown> | undefined | null,
): number | null {
  if (!googlePlacesData) return null;
  const a = pickNumberish(googlePlacesData.userRatingCount ?? googlePlacesData.user_rating_count);
  if (a != null) return a;
  const b = pickNumberish(googlePlacesData.user_ratings_total ?? googlePlacesData.userRatingsTotal);
  return b;
}

function trimNonEmptyString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
}

/** Google "About": Places (New) editorialSummary.text or legacy editorial_summary.overview. */
export function getGoogleEditorialSummary(
  googlePlacesData: Record<string, unknown> | undefined | null,
): string | null {
  if (!googlePlacesData) return null;
  const pick = (v: unknown, key: 'text' | 'overview') =>
    v && typeof v === 'object' ? trimNonEmptyString((v as Record<string, unknown>)[key]) : trimNonEmptyString(v);
  return (
    pick(googlePlacesData.editorialSummary, 'text') ??
    pick(googlePlacesData.editorial_summary, 'overview')
  );
}

/**
 * Place text for cards when the creator left no note: `ai_summary`, else Google's About
 * (`description`, or the editorial summary inside `google_places_data`).
 */
export function getPlaceSummaryFromLocation(
  location: unknown,
): { text: string; isAi: boolean } | null {
  if (!location || typeof location !== 'object') return null;
  const loc = location as { ai_summary?: unknown; description?: unknown; google_places_data?: unknown };
  const aiSummary = trimNonEmptyString(loc.ai_summary);
  if (aiSummary) return { text: aiSummary, isAi: true };
  const about =
    trimNonEmptyString(loc.description) ??
    getGoogleEditorialSummary(
      loc.google_places_data && typeof loc.google_places_data === 'object'
        ? (loc.google_places_data as Record<string, unknown>)
        : null,
    );
  return about ? { text: about, isAi: false } : null;
}

/** Places API (New) resource ids may use a `places/` prefix; Maps query_place_id expects the raw id. */
export function stripGooglePlacesResourcePrefix(placeId: string): string {
  return placeId.replace(/^places\//i, '');
}

function readGoogleMapsUriFromPlacesData(gpd: Record<string, unknown> | undefined): string | null {
  if (!gpd) return null;
  return (
    trimNonEmptyString(gpd.googleMapsUri) ??
    trimNonEmptyString(gpd.google_maps_uri) ??
    null
  );
}

/**
 * Optional structured Maps URLs from Place Details (backend).
 * Used only when root `googleMapsUri` is missing.
 */
function readGoogleMapsUriFromGoogleMapsLinks(gpd: Record<string, unknown> | undefined): string | null {
  const links = asRecord(gpd?.googleMapsLinks) ?? asRecord(gpd?.google_maps_links);
  if (!links) return null;
  return (
    trimNonEmptyString(links.googleMapsUri) ??
    trimNonEmptyString(links.google_maps_uri) ??
    trimNonEmptyString(links.uri) ??
    null
  );
}

function readLatLngFromGeoposition(
  location: Partial<Location> | undefined | null,
): { lat: number; lng: number } | null {
  const coords = location?.geoposition?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lng = Number(coords[0]);
  const lat = Number(coords[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function readLatLngFromGooglePlacesLocation(
  gpd: Record<string, unknown> | undefined,
): { lat: number; lng: number } | null {
  const loc = asRecord(gpd?.location);
  if (!loc) return null;
  const lat = pickNumberish(loc.latitude ?? loc.lat);
  const lng = pickNumberish(loc.longitude ?? loc.lng);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function buildMapsSearchCoordsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

function readDisplayNameForMapsQuery(
  gpd: Record<string, unknown> | undefined,
  location: Partial<Location> | undefined | null,
): string | null {
  const dn = gpd?.displayName;
  if (dn && typeof dn === 'object' && dn !== null && 'text' in dn) {
    const t = trimNonEmptyString((dn as { text?: unknown }).text);
    if (t) return t;
  }
  if (typeof dn === 'string') {
    const t = trimNonEmptyString(dn);
    if (t) return t;
  }
  const fromLoc = trimNonEmptyString(location?.displayName?.text) ?? trimNonEmptyString(location?.name);
  return fromLoc;
}

function readAddressForMapsQuery(
  gpd: Record<string, unknown> | undefined,
  location: Partial<Location> | undefined | null,
): string | null {
  return (
    trimNonEmptyString(gpd?.formattedAddress) ??
    trimNonEmptyString(gpd?.formatted_address) ??
    trimNonEmptyString(location?.a3_intl_address) ??
    trimNonEmptyString(location?.address) ??
    null
  );
}

function buildMapsSearchTextQueryUrl(
  gpd: Record<string, unknown> | undefined,
  location: Partial<Location> | undefined | null,
): string | null {
  const name = readDisplayNameForMapsQuery(gpd, location);
  const addr = readAddressForMapsQuery(gpd, location);
  const parts: string[] = [];
  if (name) parts.push(name);
  if (addr) parts.push(addr);
  if (parts.length === 0) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(' '))}`;
}

/**
 * Place id for `query_place_id` fallback. Prefer hotel establishment id when present (Nuitee).
 */
function pickRawPlaceIdForMapsQuery(
  location: Partial<Location> | undefined | null,
  gpd: Record<string, unknown> | undefined,
): string | null {
  const candidates = [
    trimNonEmptyString(location?.hotel_google_place_id),
    trimNonEmptyString(location?.google_place_id),
    trimNonEmptyString(gpd?.id),
    trimNonEmptyString(gpd?.place_id),
  ];
  for (const c of candidates) {
    if (c) return stripGooglePlacesResourcePrefix(c);
  }
  return null;
}

function buildQueryPlaceIdUrl(placeId: string): string {
  return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
}

/**
 * Resolves the best “open in Google Maps” URL for a location.
 *
 * Order (first match wins): `google_places_data.googleMapsUri` → optional nested URI in
 * `googleMapsLinks` → coordinate search → text search (name + address) → `query_place_id`.
 *
 * Does **not** use `reviewsUri` / `placeUri` (omitted on backend Place Details; reviews URI is not a general map link).
 *
 * @see docs/features/LISTS_AND_PLACES_FRONTEND_GUIDE.md — “Opening a place in Google Maps”
 */
export function pickPrimaryGoogleMapsUrl(location: Partial<Location> | undefined | null): string | null {
  if (!location) return null;
  const gpd = location.google_places_data as Record<string, unknown> | undefined;

  const primaryUri = readGoogleMapsUriFromPlacesData(gpd);
  if (primaryUri) return primaryUri;

  const fromLinks = readGoogleMapsUriFromGoogleMapsLinks(gpd);
  if (fromLinks) return fromLinks;

  const fromGeo = readLatLngFromGeoposition(location);
  if (fromGeo) return buildMapsSearchCoordsUrl(fromGeo.lat, fromGeo.lng);

  const fromGpdLoc = readLatLngFromGooglePlacesLocation(gpd);
  if (fromGpdLoc) return buildMapsSearchCoordsUrl(fromGpdLoc.lat, fromGpdLoc.lng);

  const textUrl = buildMapsSearchTextQueryUrl(gpd, location);
  if (textUrl) return textUrl;

  const rawId = pickRawPlaceIdForMapsQuery(location, gpd);
  if (rawId) return buildQueryPlaceIdUrl(rawId);

  return null;
}

export interface FormatPlaceCostDisplayOptions {
  /** Passed to `Intl.NumberFormat` for priceRange (e.g. `navigator.language`). */
  numberFormatLocale?: string;
}

/**
 * Single line for the combined place modal cost row: **Google data only**
 * (`priceRange` Money, else `price_level` / `priceLevel` on `google_places_data`).
 * Does not use user-editable itinerary/list `budget` or `cost` on the location root.
 */
export function formatPlaceCostDisplay(
  locationData: Partial<Location> | undefined,
  options?: FormatPlaceCostDisplayOptions,
): string | null {
  const gpd = locationData?.google_places_data as Record<string, unknown> | undefined;
  const range = formatGooglePriceRange(gpd, options?.numberFormatLocale);
  if (range) return range;
  return formatGooglePlacesPriceLevelDisplay(gpd);
}
